import { AgsRecordBuilder } from "ags/integration/builders/records/abstract-ags-record-builder";
import { AgsAuthPublicStateSchema, AgsAuthResponseSchema } from "ags/integration/schema/ags-auth-response-schema";
import { gameState } from "appworks/model/game-state";
import { Gameplay } from "appworks/model/gameplay/gameplay";
import { Record } from "appworks/model/gameplay/records/record";
import { AuthRequestPayload } from "appworks/model/gameplay/requests/auth-request-payload";
import { builderManager } from "appworks/server/builders/builder-manager";
import { RecordBuilder } from "appworks/server/builders/record-builder";
import { Services } from "appworks/services/services";
import { gameiomModel, JackpotTiers } from "gameiom/model/gameiom-model";
import { GameiomConfigSchema } from "gameiom/schema/gameiom-config-schema";
import { SpinRecord } from "slotworks/model/gameplay/records/spin-record";
import { slotDefinition } from "slotworks/model/slot-definition";
import { slotModel } from "slotworks/model/slot-model";
import { SlotBetService } from "slotworks/services/bet/slot-bet-service";
import { JurisdictionService } from "slotworks/services/jurisdiction/jurisdiction-service";
import { agsRecordUtils } from "../ags-record-utils";

export class AuthRecordBuilder extends AgsRecordBuilder implements RecordBuilder {
    public id: string = "AgsAuthRecordBuilder";

    public filter(request: any, response: any): boolean {
        if (request instanceof AuthRequestPayload) {
            const authResponse = JSON.parse(response.data) as AgsAuthResponseSchema;
            const publicState = authResponse.publicState;
            return publicState.recoveredGame === undefined;
        }
        return false;
    }

    public build(gameplay: Gameplay, request: any, response: any): Record[] {
        const authResponse: AgsAuthResponseSchema = JSON.parse(response.data);
        const publicState: AgsAuthPublicStateSchema = authResponse.publicState;
        const config: GameiomConfigSchema = authResponse.config;

        // Set the spinning reel strips to match the actual math
        const spinningStrips = publicState.spinningReelStrips;
        spinningStrips.forEach(eachSet => {
            slotDefinition.processReelSets(eachSet);
        });

        if(authResponse.config == null) {
            throw(`Config is null in the init response.`);
        }

        this.setCreditValues(config);

        gameState.autoDeductStake = !slotModel.read().bet.freeBet;

        gameiomModel.rtp = config.rtp;
        if (publicState.jackpots) {
            gameiomModel.jackpotAmounts = this.getJackpotAmounts(publicState.jackpots);
        }

        // Uncomment for testing autoplay.
        // this.setupAutoplayTesting(publicState);

        const multiplier = gameiomModel.isSocial ? 1 : 100;
        gameplay.balance = Math.round(authResponse.platform.balance * multiplier);

        if (authResponse.platform.balanceDetails) {
            gameplay.realBalance = Math.round(authResponse.platform.balanceDetails.real * multiplier);
            gameplay.freeBalance = Math.round(authResponse.platform.balanceDetails.bonus * multiplier);
        } else {
            gameplay.realBalance = gameplay.balance;
            gameplay.freeBalance = 0;
        }

        if (authResponse.publicState.startingSymbols) {
            const dummyRecord = new SpinRecord();
            dummyRecord.results = builderManager.buildResults(dummyRecord, publicState);
            dummyRecord.grid = agsRecordUtils.convertreelsToSlotworksGrid(authResponse.publicState.startingSymbols);
            return [dummyRecord] as Record[];
        }

        return [];
    }

    /**
     * This is a naive way of mapping the jackpot arrays we get from the AGS server to our JackpotTiers enum. It's
     * naive because we assume that they're sent in the following order:
     *
     * [mini, minor, major, grand]
     *
     * If fewer tiers are sent, it's assumed that the smaller values are the ones omitted.
     *
     * This will work for most games, however if you've got a special case you can override this function and map
     * them as necessary.
     */
    protected getJackpotAmounts(jackpots: number[]): Map<JackpotTiers, number> {
        return new Map<JackpotTiers, number>(
            jackpots.map((value, index) => [index, value])
        );
    }

    protected setCreditValues(config: GameiomConfigSchema): void {
        const { creditsPerLine } = slotModel.read().bet;

        creditsPerLine.currentValue = config.defaultStake;

        Services.get(SlotBetService).setCreditSize((config.defaultDenomination ?? 1));

        slotModel.write({ bet: { creditsPerLine } });

        super.setCreditValues(config);
    }

    protected setupAutoplayTesting(publicState: AgsAuthPublicStateSchema): void {
        const { autoplay } = slotModel.read();

        if(autoplay?.autoplays?.values) {
            return;
        }

        autoplay.autoplays.values = publicState.autoplay.numberOfRounds.values;
        autoplay.autoplays.defaultValue = publicState.autoplay.numberOfRounds.defaultVal;

        autoplay.singleWinLimit.values = [0, 1000, 5000, 10000, 20000, 50000];
        autoplay.singleWinLimit.defaultValue = publicState.autoplay.singleWinLimit.defaultVal;
        autoplay.singleWinLimit.currentValue = publicState.autoplay.singleWinLimit.defaultVal;

        autoplay.lossLimit.values = publicState.autoplay.lossLimit.values || [];
        autoplay.lossLimit.defaultValue = publicState.autoplay.lossLimit.defaultVal;
        autoplay.lossLimit.currentValue = publicState.autoplay.lossLimit.defaultVal;
        autoplay.lossLimitRequired = publicState.autoplay.lossLimit.lossLimitRequired;

        autoplay.totalWinLimit.values = publicState.autoplay.totalWinLimit.values || [];
        autoplay.totalWinLimit.defaultValue = publicState.autoplay.totalWinLimit.defaultVal;
        autoplay.totalWinLimit.currentValue = publicState.autoplay.totalWinLimit.defaultVal;

        gameiomModel.displayStopOnFeatureButton = true;
        autoplay.stopOnFeatureWin = false;

        slotModel.write({ autoplay });

        Services.get(JurisdictionService).setAutoplayEnabled(true);
    }
}
